import { useEffect, useState } from "react";
import { useUpdateAssignment, type MyTask } from "@/hooks/use-my-tasks";
import { DeadlineBadge } from "@/components/projects/DeadlineBadge";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Inline-editable deadline for a task assignment. Renders a DeadlineBadge when
 * a deadline is set, or a "Sett estimert deadline" affordance otherwise.
 * Click toggles a native date input that commits on blur or Enter.
 */
export function EditableDeadline({ task }: { task: MyTask }) {
  const updateMut = useUpdateAssignment();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.estimated_deadline ?? "");

  useEffect(() => {
    setValue(task.estimated_deadline ?? "");
  }, [task.estimated_deadline]);

  function commit() {
    const next = value || null;
    if (next !== (task.estimated_deadline ?? null)) {
      updateMut.mutate({ assignmentId: task.id, updates: { estimated_deadline: next } });
    }
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setValue(task.estimated_deadline ?? "");
            setEditing(false);
          }
        }}
        className="h-6 rounded border border-input bg-background px-1.5 text-xs"
      />
    );
  }

  if (task.estimated_deadline) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Klikk for å endre deadline"
      >
        <DeadlineBadge deadline={task.estimated_deadline} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary"
      title="Sett estimert deadline"
    >
      <SrIcon name="calendar-plus" className="w-3 h-3" />
      Sett estimert deadline
    </button>
  );
}

export default EditableDeadline;
